import React from "react";
import { AlertCircle, Info, Lock } from "lucide-react";
import { Input as InputBase } from "@/components/ui/input";
import {
  Select as SelectBase,
  SelectContent as SelectContentBase,
  SelectItem as SelectItemBase,
  SelectTrigger as SelectTriggerBase,
  SelectValue as SelectValueBase,
} from "@/components/ui/select";
import { useSettings } from "@/hooks/useSettings";
import { useUnitInput } from "@/hooks/useUnitInput";
import { localiseMessage, localiseNote, unitLabel, type Quantity } from "@/lib/units";

// shadcn/ui components are untyped (.jsx); relax types at the boundary
const Input: any = InputBase;
const Select: any = SelectBase;
const SelectContent: any = SelectContentBase;
const SelectItem: any = SelectItemBase;
const SelectTrigger: any = SelectTriggerBase;
const SelectValue: any = SelectValueBase;

type FieldKind = "text-input" | "number-input" | "select" | "output" | "deferred";

interface Option {
  value: string;
  label: string;
}

interface CalcFieldRowProps {
  label: string;
  unit?: string;
  /** Physical quantity of an SI field. Inputs then show / accept the
   *  interface units (Settings) and the unit label follows; outputs only get
   *  the label — the caller formats their displayValue (lib/units formatQty). */
  quantity?: Quantity;
  testId: string;
  kind: FieldKind;
  value?: string;
  displayValue?: string;
  placeholder?: string;
  options?: Option[];
  error?: string;
  /** Autocorrect note from the last blur (e.g. "40 → 35 (max)…"); shown
   *  under the field until the next edit, unless there is an error. */
  note?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}

/** Element ids derived from the (page-unique) testId — label ↔ control ↔ error. */
export const fieldId = (testId: string) => `${testId}-field`;
export const fieldErrorId = (testId: string) => `${testId}-error`;
export const fieldNoteId = (testId: string) => `${testId}-note`;

/** Small informational line under a field (autocorrect feedback). */
export function FieldNote({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <div className="mt-0.5 flex items-center justify-center gap-1" role="status">
      <Info className="h-3 w-3 shrink-0 text-[#8a5900]" aria-hidden="true" />
      <span id={id} data-testid={id} className="text-[11px] font-medium text-[#8a5900]">
        {children}
      </span>
    </div>
  );
}

/** Clear keyboard-focus ring shared by every Calculator input. */
export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--js-bright-blue)] focus-visible:ring-offset-1";

const LABEL = "w-[166px] shrink-0 text-right text-[12px] font-medium leading-tight tracking-tight text-[var(--js-text-2)]";
const UNIT = "w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-text-2)]";

const INPUT_BASE =
  `h-8 rounded-md border bg-[var(--calc-input-bg)] border-[rgba(51,66,79,0.18)] px-2 text-[12px] md:text-[12px] font-medium text-[var(--js-text)] text-center transition-colors focus-visible:border-[var(--js-bright-blue)] ${FOCUS_RING}`;
const OUTPUT_BASE =
  "flex h-8 items-center justify-center rounded-md border border-[rgba(51,66,79,0.12)] bg-[var(--calc-output-bg)] px-2 text-[12px] font-semibold text-[var(--js-text)] tabular-nums truncate";
const DEFERRED_BASE =
  "flex h-8 items-center justify-center rounded-md border border-dashed border-[rgba(51,66,79,0.20)] bg-[var(--calc-output-bg)] px-2 text-[12px] font-semibold text-[var(--js-text-3)]";

/** aria-invalid / aria-describedby for a control with an optional error. */
const errorAria = (testId: string, error?: string) => ({
  "aria-invalid": error ? true : undefined,
  "aria-describedby": error ? fieldErrorId(testId) : undefined,
});

// ---- individual control variants (kept small & focused) ----
function SelectControl({ testId, value, options, placeholder, error, onChange }: CalcFieldRowProps) {
  return (
    <Select value={value} onValueChange={(v: string) => onChange && onChange(v)}>
      <SelectTrigger
        id={fieldId(testId)}
        {...errorAria(testId, error)}
        data-testid={`${testId}-select`}
        className="h-8 rounded-md border-[rgba(51,66,79,0.18)] bg-[var(--calc-input-bg)] text-[12px] font-medium text-[var(--js-text)] focus:ring-2 focus:ring-[var(--js-bright-blue)] focus:ring-offset-1 focus:border-[var(--js-bright-blue)]"
      >
        <SelectValue placeholder={placeholder || "Select…"} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {(options || []).map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-[12px]">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function OutputControl({ testId, displayValue }: CalcFieldRowProps) {
  return (
    <div data-testid={`${testId}-output`} className={OUTPUT_BASE} title={displayValue}
      aria-labelledby={`${fieldId(testId)}-label`}>
      {displayValue ?? "—"}
    </div>
  );
}

function DeferredControl({ testId }: CalcFieldRowProps) {
  return (
    <div
      data-testid={`${testId}-deferred`}
      className={DEFERRED_BASE}
      title="Mechanical model — coming soon"
      aria-labelledby={`${fieldId(testId)}-label`}
    >
      <Lock className="mr-1 h-3 w-3 opacity-60" />
      {"—"}
    </div>
  );
}

function TextControl({ testId, value, placeholder, kind, error, quantity, onChange, onBlur }: CalcFieldRowProps) {
  const field = useUnitInput(value ?? "", quantity, onChange, onBlur);
  return (
    <Input
      id={fieldId(testId)}
      {...errorAria(testId, error)}
      data-testid={`${testId}-input`}
      value={field.value}
      placeholder={placeholder}
      inputMode={kind === "number-input" ? "decimal" : "text"}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
      onBlur={field.onBlur}
      className={`${INPUT_BASE} ${error ? "border-[var(--js-red)] shadow-[0_0_0_3px_rgba(179,0,53,0.16)]" : ""}`}
    />
  );
}

function FieldControl(props: CalcFieldRowProps) {
  switch (props.kind) {
    case "select":
      return <SelectControl {...props} />;
    case "output":
      return <OutputControl {...props} />;
    case "deferred":
      return <DeferredControl {...props} />;
    default:
      return <TextControl {...props} />;
  }
}

export const CalcFieldRow: React.FC<CalcFieldRowProps> = (rawProps) => {
  const { uiUnits } = useSettings();
  const q = rawProps.quantity;
  // Messages are built in SI by lib/validators / lib/autocorrect — localise here
  const props: CalcFieldRowProps = {
    ...rawProps,
    unit: q ? unitLabel(q, uiUnits) : rawProps.unit,
    error: localiseMessage(rawProps.error, uiUnits),
    note: localiseNote(rawProps.note, q, uiUnits),
  };
  const { label, unit, testId, error, note, kind, value } = props;
  const isControl = kind !== "output" && kind !== "deferred";
  // A sticky "!" soft-range override (lib/autocorrect) stays in the value;
  // the title explains it on hover.
  const overridden = kind === "number-input" && (value ?? "").includes("!");
  return (
    <div className="py-[3px]">
      <div className="flex items-center gap-2">
        {isControl
          ? <label htmlFor={fieldId(testId)} className={LABEL}>{label}</label>
          : <span id={`${fieldId(testId)}-label`} className={LABEL}>{label}</span>}
        <div className="min-w-0 flex-1"
          title={overridden ? "Soft-range override (!) — kept as typed. Delete the ! to apply the normal range." : undefined}>
          <FieldControl {...props} />
        </div>
        <span className={UNIT}>{unit || ""}</span>
      </div>
      {error && (
        <div className="mt-0.5 flex items-center justify-center gap-1">
          <AlertCircle className="h-3 w-3 shrink-0 text-[var(--js-red)]" aria-hidden="true" />
          <span id={fieldErrorId(testId)} data-testid={`${testId}-error`} className="text-[11px] font-medium text-[var(--js-red)]">
            {error}
          </span>
        </div>
      )}
      {!error && note && <FieldNote id={fieldNoteId(testId)}>{note}</FieldNote>}
    </div>
  );
};
